'use client';

import React from 'react';
import { motion } from 'framer-motion';
import { CalendarDaysIcon, CreditCardIcon } from '@heroicons/react/24/outline';
import type { UpcomingPaymentDue } from '@/services/CustomerApiService';
import SplitPayButton from '@/components/customer/SplitPayButton';
import { fadeInVariants } from '@/lib/animations';

interface UpcomingPaymentDuesProps {
  dues: UpcomingPaymentDue[];
  locale: string;
  title: string;
  emptyLabel: string;
  payLabel: string;
  dueLabel: string;
  index?: number;
}

function formatMoney(amount: number, symbol: string | undefined, locale: string): string {
  const loc = locale === 'tr' ? 'tr-TR' : 'en-GB';
  const formatted = amount.toLocaleString(loc, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return symbol ? `${formatted} ${symbol}` : formatted;
}

function formatDate(value: string | null | undefined, locale: string): string {
  if (!value) return '—';
  try {
    const d = new Date(value);
    const loc = locale === 'tr' ? 'tr-TR' : 'en-GB';
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(loc, { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '—';
  }
}

export default function UpcomingPaymentDues({
  dues,
  locale,
  title,
  emptyLabel,
  payLabel,
  dueLabel,
  index = 0,
}: UpcomingPaymentDuesProps) {
  return (
    <motion.section
      variants={fadeInVariants}
      initial="hidden"
      animate="visible"
      transition={{ delay: index * 0.05 }}
      className="rounded-2xl border border-gray-200/80 bg-gradient-to-br from-white via-gray-50/80 to-amber-50/40 shadow-tour-card overflow-hidden"
    >
      <div className="border-b border-gray-100 bg-white/70 px-5 py-4 flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-alsero-first/10 text-alsero-first">
          <CalendarDaysIcon className="h-5 w-5" aria-hidden />
        </span>
        <div>
          <h2 className="text-lg font-bold text-gray-900">{title}</h2>
          <p className="text-xs text-gray-500">{dueLabel}</p>
        </div>
      </div>

      {dues.length === 0 ? (
        <p className="px-5 py-8 text-sm text-gray-500 text-center">{emptyLabel}</p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {dues.map((due) => {
            const symbol = due.currency?.symbol ?? due.currency?.code;
            const isOverdue = due.due_date && new Date(due.due_date) < new Date(new Date().toDateString());

            return (
              <li key={due.id} className="px-5 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 hover:bg-white/80 transition-colors">
                <div className="min-w-0">
                  <p className="font-semibold text-gray-900 truncate">
                    {due.product_name ?? due.reservation_no ?? `#${due.reservation_id ?? due.id}`}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1.5">
                    <CalendarDaysIcon className="h-3.5 w-3.5 shrink-0" aria-hidden />
                    <span className={isOverdue ? 'text-red-600 font-medium' : ''}>
                      {formatDate(due.due_date, locale)}
                    </span>
                    {due.reservation_no && (
                      <span className="text-gray-400">· {due.reservation_no}</span>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <div className="text-right">
                    <p className="text-sm font-bold text-gray-900 tabular-nums">
                      {formatMoney(due.total_due, symbol, locale)}
                    </p>
                    {(due.late_fee_amount ?? 0) > 0 && (
                      <p className="text-[10px] text-amber-700">
                        + {formatMoney(due.late_fee_amount ?? 0, symbol, locale)} fee
                      </p>
                    )}
                  </div>
                  {due.reservation_id && due.status !== 'completed' && due.status !== 'paid' ? (
                    <SplitPayButton
                      reservationId={due.reservation_id}
                      splitId={due.id}
                      paymentUrl={due.payment_url}
                      label={payLabel}
                      loadingLabel={payLabel}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-alsero-first px-3 py-2 text-xs font-semibold text-white hover:bg-alsero-second transition-colors disabled:opacity-60"
                    />
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </motion.section>
  );
}
